import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import ChatIcon from '@mui/icons-material/Chat';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export type TripBuildMode = 'wizard' | 'text';

interface TripModeSelectorProps {
  onSelectMode: (mode: TripBuildMode) => void;
}

function TripModeSelector({ onSelectMode }: TripModeSelectorProps) {
  const wizardFeatures = [
    'Select countries & cities',
    'Choose travel dates',
    'Set budget & preferences',
  ];

  const textFeatures = [
    'Describe in your own words',
    'AI extracts your preferences',
    'Instant itinerary generation',
  ];

  return (
    <Box sx={{ py: 2 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 5 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          How would you like to plan?
        </Typography>
      </Box>

      {/* Cards Section */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Guided Controls Card */}
        <Card
          sx={{
            width: { xs: '100%', sm: 340 },
            maxWidth: 340,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            border: '2px solid',
            borderColor: 'grey.200',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.15)',
              borderColor: 'primary.main',
            },
          }}
        >
          <CardActionArea onClick={() => onSelectMode('wizard')} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Icon & Title Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TuneIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Guided Controls
                </Typography>
              </Box>

              {/* Description */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Use intuitive controls to select your destinations, dates, and preferences. Perfect if you know what you want.
              </Typography>

              {/* Features */}
              <Box sx={{ mb: 2 }}>
                {wizardFeatures.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Time Estimate */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'grey.100',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  About 2-3 minutes
                </Typography>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Natural Language Card */}
        <Card
          sx={{
            width: { xs: '100%', sm: 340 },
            maxWidth: 340,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            border: '2px solid',
            borderColor: 'grey.200',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.15)',
              borderColor: 'primary.main',
            },
          }}
        >
          <CardActionArea onClick={() => onSelectMode('text')} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Icon & Title Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ChatIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Conversational
                </Typography>
              </Box>

              {/* Description */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Simply describe your dream trip in your own words. Our AI understands and creates your perfect itinerary.
              </Typography>

              {/* Features */}
              <Box sx={{ mb: 2 }}>
                {textFeatures.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Time Estimate */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'grey.100',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  About 1 minute
                </Typography>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    </Box>
  );
}

export default TripModeSelector;
